import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { lectures } from "@/mocks/lectures";
import { motion, AnimatePresence } from "motion/react";

export default function PopularSlider() {
  const top3 = [...lectures].sort((a, b) => b.views - a.views).slice(0, 3);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
      className="mb-10"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">🔥</span>
        <h2 className="text-base font-bold text-[#1C1917]">
          이번 주 인기 강좌 Top 3
        </h2>
        <span className="text-xs text-[#8C8780] ml-1">가장 많이 시청한 강의</span>
      </div>

      <div className="relative overflow-hidden rounded-2xl bg-white border border-[#E8E5E0]">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.4, ease: "easeOut" as const }}
          >
            <Link
              to={`/lectures/${top3[active].id}`}
              className="flex flex-col md:flex-row"
            >
              <div className="relative md:w-2/5 aspect-video md:aspect-auto overflow-hidden">
                <img loading="lazy" decoding="async"
                  src={top3[active].thumbnail}
                  alt={top3[active].title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent md:bg-gradient-to-t md:from-black/60 md:to-transparent" />
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 text-xs font-bold bg-rose-500 text-white rounded-full">
                    TOP {active + 1}
                  </span>
                </div>
                <div className="absolute bottom-3 left-3">
                  <span className="px-2 py-0.5 text-xs font-medium bg-black/70 text-white rounded">
                    {top3[active].duration}
                  </span>
                </div>
              </div>
              <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                    top3[active].difficulty === "초급"
                      ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                      : top3[active].difficulty === "중급"
                      ? "bg-amber-50 text-amber-600 border border-amber-200"
                      : "bg-rose-50 text-rose-600 border border-rose-200"
                  }`}>
                    {top3[active].difficulty}
                  </span>
                  <span className="px-2 py-0.5 text-[10px] font-medium bg-[#C4956A]/10 text-[#C4956A] rounded-full border border-[#C4956A]/20">
                    {top3[active].categoryLabel}
                  </span>
                  {top3[active].isFree && (
                    <span className="px-2 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700 rounded-full border border-emerald-200">
                      무료
                    </span>
                  )}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-[#1C1917] mb-2 leading-snug">
                  {top3[active].title}
                </h3>
                <p className="text-sm text-[#8C8780] mb-4 line-clamp-2 leading-relaxed">
                  {top3[active].description}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mb-4">
                  {top3[active].tags.map((tag) => (
                    <span key={tag} className="text-[11px] text-[#8C8780] hover:text-[#C4956A] transition-colors cursor-default">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-4 text-xs text-[#8C8780]">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {top3[active].views.toLocaleString()}회
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-thumb-up-line"></i>
                    {top3[active].likes.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-star-line text-[#C4956A]"></i>
                    {top3[active].instructor}
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        </AnimatePresence>

        {/* Dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {[0, 1, 2].map((i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                i === active ? "bg-[#C4956A] w-6" : "bg-white/60 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
}